import { useEffect } from "react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Dialog } from "@headlessui/react";
import Modal from "../../components/Modal";
import {
    Bars3Icon,
    ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import {
    MapPinIcon as SolidMapPinIcon,
    CakeIcon,
} from "@heroicons/react/24/solid";
import api from "../../api";
import Sidebar from "../../components/Sidebar";
import Breadcrumbs from "../../components/Breadcrumbs";
import FileInput from "../../components/FileInput";
import Table from "../../components/Table";

const columns = [
    { name: "uploadedBy", displayText: "Subido por", truncate: true },
    { name: "uploadDate", displayText: "Fecha" },
    { name: "name", displayText: "Nombre del archivo", truncate: true },
];

export default function PatientDocumentsPage() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [patient, setPatient] = useState();
    const [cities, setCities] = useState([]);
    const [patientDocument, setPatientDocument] = useState({ documents: [] });
    const [loading, setLoading] = useState(true);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(undefined);
    const [userId, setUserId] = useState(
        JSON.parse(localStorage.getItem("BDOuserData"))?.id,
    );
    const [openEdit, setOpenEdit] = useState(false);
    const [editName, setEditName] = useState("");
    const [selectedDocument, setSelectedDocument] = useState(undefined);
    const [openDelete, setOpenDelete] = useState(false);
    const { idPatient } = useParams();

    const handleSubmit = async () => {
        setSuccess(false);
        setLoadingSubmit(true);

        try {

            const uploadPromises = patientDocument.documents.map((doc) => {
                const finalName = `${doc.name}.${doc.extension}`;

                const formData = new FormData();
                formData.append("patient", idPatient.toString());
                formData.append("document", doc.file);
                formData.append("name", finalName);
                formData.append("uploadedBy", userId);

                return api.documents.create(formData).then((response) => {
                    setDocuments((prev) => [
                        ...prev,
                        {
                            id: response.id,
                            name: response.name,
                            uploadedBy: response.uploadedBy.name,
                            uploadDate: new Date(
                                response.uploadDate,
                            ).toLocaleDateString(),
                            document: response.document,
                        },
                    ]);
                });
            });

            await Promise.all(uploadPromises);

            setSuccess(true);
            setPatientDocument({ documents: [] });
        } catch (error) {
            console.error("Error al subir documentos:", error);
            setError(error.message);
        } finally {
            setLoadingSubmit(false);
        }
    };

    const startEditDocument = (doc) => {
        setSelectedDocument(doc);
        const dotIndex = doc.name.lastIndexOf(".");
        const baseName = dotIndex > 0 ? doc.name.slice(0, dotIndex) : doc.name;
        const ext = dotIndex > 0 ? doc.name.slice(dotIndex + 1) : "";

        setEditName(baseName);
        setOpenEdit(true);
    };

    const handleRename = async () => {
        if (!selectedDocument) return;

        try {
            const originalExt = selectedDocument.name.includes(".")
                ? selectedDocument.name.split(".").pop()
                : "";
            const finalName = originalExt
                ? `${editName}.${originalExt}`
                : editName;

            await api.documents.update(
                selectedDocument.id,
                { name: finalName },
                true,
            );

            setDocuments((prev) =>
                prev.map((doc) =>
                    doc.id === selectedDocument.id
                        ? { ...doc, name: finalName }
                        : doc,
                ),
            );

            setOpenEdit(false);
            setSelectedDocument(undefined);
        } catch (error) {
            setError(error.message);
        }
    };

    const handleFileSelect = (files) => {
        const MAX_SIZE_BYTES = 20 * 1024 * 1024;
        const tooLarge = files.filter((file) => file.size > MAX_SIZE_BYTES);
        const allowed = files.filter((file) => file.size <= MAX_SIZE_BYTES);

        if (tooLarge.length > 0) {
            const names = tooLarge.map((f) => f.name).join(", ");
            setError({
                document: `El archivo supera el tamaño máximo permitido (20 MB): ${names}`,
            });
        } else {
            setError(undefined);
        }

        if (allowed.length === 0) return;

        const mapped = allowed.map((file) => ({
            file,
            name: file.name.split(".")[0],
            extension: file.name.split(".").pop(),
        }));

        setPatientDocument((prev) => ({
            ...prev,
            documents: [...prev.documents, ...mapped],
        }));
    };

    const handleDelete = async () => {
        console.log(selectedDocument);
        api.documents
            .delete(selectedDocument.id)
            .then((response) => {
                api.documents.getMulti(null, idPatient).then((response) => {
                    setDocuments(
                        response.map((element) => ({
                            id: element.id,
                            name: element.name,
                            uploadedBy: element.uploadedBy.name,
                            uploadDate: new Date(
                                element.uploadDate,
                            ).toLocaleDateString(),
                            document: element.document,
                        })),
                    );
                    setOpenDelete(false);
                    setSelectedDocument(undefined);
                });
            })
            .catch((error) => setError(error.message));
    };

    const pages = [
        { name: "Pacientes", href: "/patients", current: false },
        {
            name: "Detalle  del paciente",
            href: `/patients/${idPatient}`,
            current: false,
        },
        { name: "Adjuntar archivos", href: `#`, current: true },
    ];

    useEffect(() => {
        api.cities.getMulti().then((resp) => {
            setCities(resp);
            api.patients.getOne(idPatient).then((response) => {
                setPatient({
                    ...response,
                    name: response.firstName + " " + response.lastName,
                    city: resp.find((city) => city.id === response.city + 1)
                        ?.name,
                });
                setLoading(false);
            });
            api.documents
                .getMulti(null, null, null, null, { patient: idPatient })
                .then((response) => {
                    setDocuments(
                        response.map((element) => ({
                            id: element.id,
                            name: element.name,
                            uploadedBy: element.uploadedBy
                                ? element.uploadedBy.name
                                : "--",
                            uploadDate: new Date(
                                element.uploadDate,
                            ).toLocaleDateString(),
                            document: element.document,
                        })),
                    );
                });
        });
    }, []);

    const editModal = (
        <>
            <div>
                <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title
                        as="h3"
                        className="text-lg leading-6 font-medium text-gray-900 dark:text-white"
                    >
                        Renombrar archivo
                    </Dialog.Title>
                    <div className="mt-4 px-4 flex items-center gap-2">
                        <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="w-full border rounded-md px-3 py-2 text-black"
                        />
                        {selectedDocument && (
                            <span className="text-sm text-gray-500">
                                .{selectedDocument.name.split(".").pop()}
                            </span>
                        )}
                    </div>
                </div>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary sm:col-start-2 sm:text-sm"
                    onClick={handleRename}
                >
                    Guardar
                </button>
                <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => {
                        setOpenEdit(false);
                        setSelectedDocument(undefined);
                    }}
                >
                    Cancelar
                </button>
            </div>
        </>
    );

    const deletionModal = (
        <>
            <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                    <ExclamationTriangleIcon
                        className="h-6 w-6 text-red"
                        aria-hidden="true"
                    />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title
                        as="h3"
                        className="text-lg leading-6 font-medium text-gray-900 dark:text-white"
                    >
                        Eliminar archivo
                    </Dialog.Title>
                    <div className="mt-2">
                        <p className="text-sm text-gray-500 dark:text-gray-200">
                            ¿Estás seguro de que quieres eliminar{" "}
                            {selectedDocument?.name}?
                        </p>
                    </div>
                </div>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red text-base font-medium text-white hover:bg-red-500 sm:col-start-2 sm:text-sm"
                    onClick={() => handleDelete()}
                >
                    Eliminar
                </button>
                <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => (
                        setOpenDelete(false),
                        setSelectedDocument(undefined)
                    )}
                >
                    Cancelar
                </button>
            </div>
        </>
    );

    return (
        <div className="bg-gray-50 dark:bg-gray-800 h-screen">
            <Modal
                size="sm:max-w-lg w-full"
                content={deletionModal}
                open={openDelete}
                setOpen={setOpenDelete}
            />
            <Sidebar
                currentScreen={"Pacientes"}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
            />
            {loading ? (
                <div className="flex h-full items-center justify-center ">
                    <div className="w-16 h-16 border-b-2 border-blue-900 rounded-full animate-spin"></div>
                </div>
            ) : (
                <div className="md:pl-64 flex flex-col bg-gray-100 dark:bg-gray-800 flex-1">
                    <div className="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-gray-100 dark:bg-gray-800">
                        <button
                            type="button"
                            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
                            onClick={() => setSidebarOpen(true)}
                        >
                            <span className="sr-only">Open sidebar</span>
                            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                        </button>
                    </div>

                    <div className="flex flex-col flex-1 pb-4 ml-6 mt-6">
                        <Breadcrumbs pages={pages} />
                        <div className="ml-4">
                            <h1 className="mt-6 text-3xl font-bold leading-9 text-gray-900 dark:text-white">
                                {patient?.name}
                            </h1>
                            <div className="flex flex-wrap items-center mt-2 gap-y-1">
                                <div>
                                    <span className="text-sm leading-5 font-medium text-gray-500">
                                        C.I. {patient?.govId}
                                    </span>
                                </div>
                                <div className="ml-6">
                                    <CakeIcon className="inline-block h-5 w-5 text-gray-500" />
                                    <span className="text-sm leading-5 ml-2 font-medium text-gray-500">
                                        {patient?.age} años
                                    </span>
                                </div>
                                {patient.city ? (
                                    <div className="ml-6">
                                        <SolidMapPinIcon className="inline-block h-5 w-5 text-gray-500" />
                                        <span className="text-sm leading-5 ml-2 font-medium text-gray-500">
                                            {patient.city}
                                        </span>
                                    </div>
                                ) : null}
                                <div className="ml-4">
                                    <span className="text-sm leading-5font-medium text-gray-500">
                                        |{" "}
                                    </span>
                                    <span className="text-sm leading-5 ml-4 font-medium text-gray-500">
                                        Última visita:{" "}
                                        {patient?.last_visit
                                            ? patient.last_visit
                                            : "--"}{" "}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <main className="flex-1 bg-gray-50 dark:bg-gray-800 px-6 py-10 text-gray-900 dark:text-white">
                        <p className="font-bold text-[30px] text-[#4B5563] dark:text-gray-200 leading-[36px]">
                            Archivos adjuntos
                        </p>
                        <div className="bg-white dark:bg-gray-900 mt-4 px-8 py-6 max-w-screen shadow border flex-1 border-gray-300 dark:border-gray-700 rounded-lg overflow-auto">
                            <div className="flex flex-col sm:flex-row gap-x-6">
                                <FileInput
                                    errorMessage={error?.document}
                                    multiple={true}
                                    labelClassName="text-sm font-medium leading-[20px] text-[#374151] dark:text-gray-200"
                                    label="Archivo"
                                    buttonText="Adjuntar archivo"
                                    handleChange={(name, files) =>
                                        handleFileSelect(files)
                                    }
                                    name="document"
                                />
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                    {patientDocument.documents.map(
                                        (doc, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center gap-2 mb-2"
                                            >
                                                <input
                                                    type="text"
                                                    value={doc.name}
                                                    onChange={(e) => {
                                                        const updated = [
                                                            ...patientDocument.documents,
                                                        ];
                                                        updated[index].name =
                                                            e.target.value;
                                                        setPatientDocument({
                                                            ...patientDocument,
                                                            documents: updated,
                                                        });
                                                    }}
                                                    className="border rounded p-1 text-black"
                                                />
                                                <span className="text-sm text-gray-500">
                                                    .{doc.extension}
                                                </span>
                                            </div>
                                        ),
                                    )}
                                </div>
                                <div className="flex flex-col gap-1">
                                    <button
                                        type="button"
                                        onClick={() => handleSubmit()}
                                        disabled={loadingSubmit}
                                        className="flex justify-center h-fit  gap-2 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary bg-white disabled:bg-gray-200 disabled:cursor-wait"
                                    >
                                        {loadingSubmit ? (
                                            <div className="w-5 h-5 border-b-2 border-blue-900 dark:border-primary rounded-full animate-spin"></div>
                                        ) : (
                                            "Añadir"
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setPatientDocument({
                                                documents: [],
                                            });
                                            setError(null);
                                        }}
                                        disabled={loadingSubmit}
                                        className="flex justify-center h-fit  gap-2 py-2 px-4 border rounded-md shadow-sm text-sm font-medium text-primary bg-transparent border-primary disabled:bg-gray-200 disabled:cursor-not-allowed"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                            <div className="mt-6">
                                <Table
                                    actiontext="Ver"
                                    delete={true}
                                    setSelectedDocument={setSelectedDocument}
                                    setOpenDelete={setOpenDelete}
                                    document={true}
                                    columns={columns}
                                    rows={documents}
                                    openModal={startEditDocument}
                                    openModalText="Renombrar"
                                    actionCellClassName="px-2 py-1 whitespace-nowrap text-center text-sm "
                                />
                                <Modal
                                    size="sm:max-w-lg w-full text-gray-900 dark:text-white"
                                    content={editModal}
                                    open={openEdit}
                                    setOpen={setOpenEdit}
                                />
                            </div>
                        </div>
                    </main>
                </div>
            )}
        </div>
    );
}
